import { boxMullerNormal, createRng, type RngFn } from '../shared/rng.util';
import type { TestGeneratorConfig } from './test-generator.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface GeneratedTestColumns {
  readonly columns: GeneratedColumn[];
}

export interface GeneratedColumn {
  readonly header: string;
  readonly colType: 'number' | 'text';
  readonly values: Array<number | string | null>;
}

export function generateTestData(config: TestGeneratorConfig): GeneratedTestColumns {
  const rng = createRng(config.seed);

  switch (config.testType) {
    case 'one-sample-t':
      return generateOneSampleT(config, rng);
    case 'two-sample-t':
      return generateTwoSampleT(config, rng);
    case 'paired-t':
      return generatePairedT(config, rng);
    case 'chi-squared-independence':
      return generateChiSquaredIndependence(config, rng);
    case 'chi-squared-gof':
      return generateChiSquaredGof(config, rng);
    case 'sign-test':
      return generateSignTest(config, rng);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roundTo(value: number, dp: number): number {
  const factor = Math.pow(10, dp);
  return Math.round(value * factor) / factor;
}

function multinomial(n: number, probs: number[], rng: RngFn): number[] {
  const counts = new Array(probs.length).fill(0);
  for (let k = 0; k < n; k++) {
    const u = rng();
    let cumulative = 0;
    for (let i = 0; i < probs.length; i++) {
      cumulative += probs[i];
      if (u <= cumulative) {
        counts[i]++;
        break;
      }
    }
    // Safety: if rounding means we didn't place it, add to last bin
    if (counts.reduce((a, b) => a + b, 0) < k + 1) {
      counts[counts.length - 1]++;
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// One-sample t-test
// ---------------------------------------------------------------------------

function generateOneSampleT(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize, effectStrength, hypothesizedMean, decimalPlaces } = config;
  const d = effectStrength * 2; // Cohen's d: 0 → 2
  const sigma = Math.max(hypothesizedMean * 0.15, 1);
  const trueMean = hypothesizedMean + d * sigma;

  const values: number[] = [];
  for (let i = 0; i < sampleSize; i++) {
    values.push(roundTo(boxMullerNormal(trueMean, sigma, rng), decimalPlaces));
  }

  return {
    columns: [{ header: 'Value', colType: 'number', values }],
  };
}

// ---------------------------------------------------------------------------
// Two-sample t-test
// ---------------------------------------------------------------------------

function generateTwoSampleT(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize, effectStrength, equalGroupSizes, decimalPlaces } = config;
  const d = effectStrength * 2;
  const sigma = 15;
  const mu1 = 100;
  const mu2 = 100 + d * sigma;

  let n1: number;
  let n2: number;
  if (equalGroupSizes) {
    n1 = Math.floor(sampleSize / 2);
    n2 = sampleSize - n1;
  } else {
    n1 = Math.floor(sampleSize * 0.6);
    n2 = sampleSize - n1;
  }

  const maxLen = Math.max(n1, n2);
  const group1: Array<number | null> = [];
  const group2: Array<number | null> = [];

  for (let i = 0; i < maxLen; i++) {
    group1.push(i < n1 ? roundTo(boxMullerNormal(mu1, sigma, rng), decimalPlaces) : null);
    group2.push(i < n2 ? roundTo(boxMullerNormal(mu2, sigma, rng), decimalPlaces) : null);
  }

  return {
    columns: [
      { header: 'Group 1', colType: 'number', values: group1 },
      { header: 'Group 2', colType: 'number', values: group2 },
    ],
  };
}

// ---------------------------------------------------------------------------
// Paired t-test
// ---------------------------------------------------------------------------

function generatePairedT(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize, effectStrength, decimalPlaces } = config;
  const d = effectStrength * 2;
  const sigmaD = 8;
  const meanDiff = d * sigmaD;
  const sigmaBase = 15;
  const muBase = 100;

  const before: number[] = [];
  const after: number[] = [];

  for (let i = 0; i < sampleSize; i++) {
    const b = boxMullerNormal(muBase, sigmaBase, rng);
    const diff = boxMullerNormal(meanDiff, sigmaD, rng);
    before.push(roundTo(b, decimalPlaces));
    after.push(roundTo(b + diff, decimalPlaces));
  }

  return {
    columns: [
      { header: 'Before', colType: 'number', values: before },
      { header: 'After', colType: 'number', values: after },
    ],
  };
}

// ---------------------------------------------------------------------------
// Chi-squared test for independence
// ---------------------------------------------------------------------------

function generateChiSquaredIndependence(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize: N, effectStrength, contingencyRows: R, contingencyCols: C } = config;

  // Build cell probabilities with controlled deviation from independence
  const probs: number[] = [];
  const basePr = 1 / (R * C);

  for (let i = 0; i < R; i++) {
    for (let j = 0; j < C; j++) {
      // Checkerboard perturbation — alternating cells get + or − shift
      const sign = (i + j) % 2 === 0 ? 1 : -1;
      const perturbation = sign * effectStrength * 0.3 * basePr;
      probs.push(Math.max(basePr + perturbation, basePr * 0.05));
    }
  }

  // Normalize
  const total = probs.reduce((s, p) => s + p, 0);
  for (let k = 0; k < probs.length; k++) {
    probs[k] /= total;
  }

  // Generate multinomial counts
  const flatCounts = multinomial(N, probs, rng);

  // Reshape into R × C matrix
  const counts: number[][] = [];
  for (let i = 0; i < R; i++) {
    counts.push(flatCounts.slice(i * C, (i + 1) * C));
  }

  // Build columns: first column is row labels, then one number column per category
  const rowLabels = Array.from({ length: R }, (_, i) => `Row ${i + 1}`);
  const colLabels = Array.from({ length: C }, (_, j) => `Col ${j + 1}`);

  const columns: GeneratedColumn[] = [{ header: 'Category', colType: 'text', values: rowLabels }];

  for (let j = 0; j < C; j++) {
    columns.push({
      header: colLabels[j],
      colType: 'number',
      values: Array.from({ length: R }, (_, i) => counts[i][j]),
    });
  }

  return { columns };
}

// ---------------------------------------------------------------------------
// Chi-squared goodness of fit
// ---------------------------------------------------------------------------

function generateChiSquaredGof(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize: N, effectStrength, gofCategories: K } = config;

  // Expected proportions: uniform
  const expectedPr = 1 / K;

  // Perturb observed probabilities: alternating high/low
  const observedPr: number[] = [];
  for (let i = 0; i < K; i++) {
    const sign = i % 2 === 0 ? 1 : -1;
    const perturbation = sign * effectStrength * 0.4 * expectedPr;
    observedPr.push(Math.max(expectedPr + perturbation, expectedPr * 0.05));
  }

  // Normalize
  const total = observedPr.reduce((s, p) => s + p, 0);
  for (let k = 0; k < K; k++) {
    observedPr[k] /= total;
  }

  // Generate multinomial counts
  const counts = multinomial(N, observedPr, rng);

  // Build columns
  const categories = Array.from({ length: K }, (_, i) => `Cat ${i + 1}`);

  return {
    columns: [
      { header: 'Category', colType: 'text', values: categories },
      { header: 'Observed', colType: 'number', values: counts },
    ],
  };
}

// ---------------------------------------------------------------------------
// Sign test
// ---------------------------------------------------------------------------

function generateSignTest(config: TestGeneratorConfig, rng: RngFn): GeneratedTestColumns {
  const { sampleSize, effectStrength, decimalPlaces } = config;

  // p = probability that sample2 > sample1
  const p = 0.5 + effectStrength * 0.45;
  const sigmaBase = 10;
  const muBase = 50;
  const diffMag = 5; // magnitude of typical difference

  const sample1: number[] = [];
  const sample2: number[] = [];

  for (let i = 0; i < sampleSize; i++) {
    const base = boxMullerNormal(muBase, sigmaBase, rng);
    const isPositive = rng() < p;
    const absDiff = Math.abs(boxMullerNormal(diffMag, diffMag * 0.4, rng));
    const diff = isPositive ? absDiff : -absDiff;

    sample1.push(roundTo(base, decimalPlaces));
    sample2.push(roundTo(base + diff, decimalPlaces));
  }

  return {
    columns: [
      { header: 'Sample 1', colType: 'number', values: sample1 },
      { header: 'Sample 2', colType: 'number', values: sample2 },
    ],
  };
}
